import { FALL_DMG_THRESHOLD, GRAVITY, JUMP_GRAVITY_SCALE, WORLD_H, WORLD_W } from '../constants.js';
import type { Entity } from '../types.js';
import { isSolid, lineHit, surfaceNormal } from '../terrain/mask.js';

/** Half-width of the feet/hip clearance column. */
export const CHAR_W = 6;
/** Torso/head sample heights above the feet baseline. */
const CLEAR_Y = [5, 17, 29];
/**
 * Character-vs-character contact box (feet-anchored): bodies shove apart when
 * these overlap side-on, and the upper body settles onto the lower one's
 * head-top when the feet reach `y - BODY_H`. Taller than the visual body so
 * standing on someone reads as standing ON them, not floating above them.
 */
export const BODY_HALF_W = 8;
export const BODY_H = 34;

export function hasClearance(terrain: Uint8Array, x: number, y: number): boolean {
  for (const h of CLEAR_Y) {
    if (isSolid(terrain, x, y - h)) return false;
  }
  // Hip width: block squeezing through 12px gaps.
  if (isSolid(terrain, x - CHAR_W, y - 17) || isSolid(terrain, x + CHAR_W, y - 17)) return false;
  return true;
}

/**
 * Ground contact: solid just below the feet across the stance width.
 * A probe only counts when the pixel above it is EMPTY — a wall face beside
 * the body is solid at every height, so without that check pressing into a
 * wall reads as "standing" and gravity never runs (the classic wall-stick).
 */
export function isGrounded(terrain: Uint8Array, e: { x: number; y: number }): boolean {
  const on = (px: number, py: number): boolean =>
    isSolid(terrain, px, py) && !isSolid(terrain, px, py - 4);
  return on(e.x, e.y + 3) || on(e.x - 5, e.y + 3) || on(e.x + 5, e.y + 3);
}

/**
 * Horizontal walking with auto step-up (≤8px). Returns false when blocked.
 * Does not touch vy — the vertical phase owns gravity.
 */
export function walk(
  terrain: Uint8Array,
  e: Entity,
  dir: 1 | -1,
  dt: number,
  speed: number,
): boolean {
  const dx = dir * speed * dt;
  const nx = e.x + dx;
  if (hasClearance(terrain, nx, e.y)) {
    e.x = nx;
    return true;
  }
  // Small step up (curbs, pebbles, dug ledges).
  if (hasClearance(terrain, nx, e.y - 8)) {
    e.x = nx;
    e.y -= 8;
    return true;
  }
  return false;
}

export interface VerticalResult {
  /** Impact speed on landing (>0 once); 0 when no landing this tick. */
  landSpeed: number;
  hitWall: boolean;
}

/**
 * Gravity + swept collision for a character (airborne or ground-checking).
 * Landing converts impact into fall damage info (returned, not applied).
 */
export function stepVertical(
  terrain: Uint8Array,
  e: Entity,
  dt: number,
  wind: number,
): VerticalResult {
  const res: VerticalResult = { landSpeed: 0, hitWall: false };

  if (e.onGround) {
    if (isGrounded(terrain, e)) {
      e.vx = 0;
      e.vy = 0;
      return res;
    }
    e.onGround = false;
  }

  e.vy += GRAVITY * dt;
  e.vx += wind * 0.2 * dt;

  const x1 = e.x + e.vx * dt;
  const y1 = e.y + e.vy * dt;
  const hit = lineHit(terrain, e.x, e.y, x1, y1);

  if (hit) {
    const n = surfaceNormal(terrain, hit.x, hit.y);
    const impactSpeed = Math.hypot(e.vx, e.vy);
    e.x = hit.x + n.x * 1.5;
    e.y = hit.y + n.y * 1.5;

    const isGround = n.y < -0.55; // surface normal points up
    const vn = e.vx * n.x + e.vy * n.y;
    if (vn < 0) {
      e.vx -= vn * n.x; // remove normal component (no character bounce)
      e.vy -= vn * n.y;
    }
    e.vx *= 0.7; // scuff on contact

    if (isGround) {
      e.onGround = true;
      e.vx = 0;
      e.vy = 0;
      e.lastLandSpeed = impactSpeed; // consumed by applyFall on this landing
      res.landSpeed = impactSpeed;
    } else {
      res.hitWall = true;
    }
  } else {
    e.x = x1;
    e.y = y1;
  }
  return res;
}

/** Fall damage from a landing impact; 0 when survivable. */
export function fallDamage(impactSpeed: number): number {
  if (impactSpeed <= FALL_DMG_THRESHOLD) return 0;
  return Math.max(1, Math.floor((impactSpeed - FALL_DMG_THRESHOLD) / 12));
}

/** Radial knockback impulse from an explosion. */
export function knockback(
  e: Entity,
  cx: number,
  cy: number,
  strength: number,
): void {
  const dx = e.x - cx;
  const dy = e.y - 18 - cy;
  const d = Math.hypot(dx, dy) || 1;
  e.vx += (dx / d) * strength;
  e.vy += (dy / d) * strength - strength * 0.35; // upward bias = flippy fun
  e.script = null; // a blast rips you off the jump arc — physics takes over
  e.onGround = false;
}

/**
 * Step an aimed jump script. Returns 'done' | 'flying' | 'landed'
 * (landed = collision interrupted the arc; normal physics take over next tick)
 * | 'bounce' (hit the ground with pace and hopped back up — still airborne).
 */
export function stepScript(
  terrain: Uint8Array,
  e: Entity,
  dt: number,
  wind: number,
): 'done' | 'flying' | 'landed' | 'bounce' {
  const s = e.script;
  if (!s) return 'done';
  s.t += dt;

  // jump: ballistic with gravity + a touch of wind. Light gravity
  // (JUMP_GRAVITY_SCALE) gives the arc cartoon hang-time — the solver that
  // aimed it uses the same scale, or the preview and the flight drift apart.
  s.vx += wind * 0.5 * dt;
  s.vy += GRAVITY * JUMP_GRAVITY_SCALE * dt;
  const x1 = e.x + s.vx * dt;
  const y1 = e.y + s.vy * dt;

  // One landing path for every way the arc can end on terrain: snap to the
  // surface, report the full first impact (fall damage / juice read it), and
  // rebound — a jump touchdown is NEVER a flat thud, it always pops a little.
  const land = (hx: number, hy: number): 'landed' | 'bounce' => {
    const impact = Math.hypot(s.vx, s.vy);
    const n = surfaceNormal(terrain, hx, hy);
    e.x = hx + n.x * 1.5;
    e.y = hy + n.y * 1.5;
    e.lastLandSpeed = impact; // the land event carries the full first impact
    e.script = null;
    if (n.y < -0.55) {
      // Mostly-vertical hop with a whisper of forward skid, so the character
      // rests close to where the previewed arc ended. The floor guarantees a
      // visible pop even on the softest landing.
      if (impact >= 55) {
        e.vx = s.vx * 0.2;
        e.vy = -Math.max(105, impact * 0.3);
        e.onGround = false;
        return 'bounce';
      }
      e.onGround = true;
      e.vx = 0;
      e.vy = 0;
      return 'landed';
    }
    // Smacked a wall (or a ceiling) mid-arc: kick OFF it instead of
    // plastering onto it. Reflect the momentum about the surface normal so
    // the character bounces clear and falls — never sticks with no gravity.
    const vn = s.vx * n.x + s.vy * n.y;
    if (vn < 0) {
      e.vx = s.vx - 1.55 * vn * n.x; // restitution ≈0.55 off the wall
      e.vy = s.vy - 1.55 * vn * n.y;
    } else {
      e.vx = s.vx;
      e.vy = s.vy;
    }
    e.onGround = false;
    return 'landed';
  };

  // Sweep from one row above the feet: a character stands *in* the topmost
  // solid row, so a sweep from the exact standing position would hit the
  // ground it is already touching and cancel the jump on its first tick.
  const hit = lineHit(terrain, e.x, e.y - 1, x1, y1);
  if (hit) return land(hit.x, hit.y);

  e.x = x1;
  e.y = y1;

  // Arrived near the aimed spot. Land ONLY when ground is under the feet: a
  // tap in mid-sky used to hand over to full-gravity physics here, which
  // dropped the character from the apex like a stone (fall damage and a dead
  // stop, no bounce). Now the arc coasts through the point on the same light
  // gravity and ends the way every jump should — a real landing, with a hop.
  const distToTarget = Math.hypot(s.toX - e.x, s.toY - e.y);
  if (distToTarget < 12 && s.t > 0.15) {
    const ground = lineHit(terrain, e.x, e.y, e.x, e.y + 16);
    if (ground) return land(ground.x, ground.y);
  }

  // Safety valves: never leave the world, never fly forever (a launch aimed
  // past the map edge still has to hand back so the OOB check can fire).
  if (e.x < -80 || e.x > WORLD_W + 80 || e.y > WORLD_H + 100 || e.y < -800 || s.t > 12) {
    e.vx = s.vx;
    e.vy = s.vy;
    e.script = null;
    e.onGround = false;
    return 'done';
  }
  return 'flying';
}

/** Consume the fall-impact speed recorded by the most recent landing. */
export function takeLastLandSpeed(e: Entity): number {
  const slot = e.lastLandSpeed;
  e.lastLandSpeed = 0;
  return slot;
}
